import { useIsFetching, useQuery } from "@tanstack/react-query"

import { GetTimeRequestResponse } from "utils/interfaces"
import { getTimeRequest, waitTimeRequestStatusUpdate } from "modules/Booking/common/utils"
import { Callbacks } from "modules/BookingCommon/common/queries/callbacks"
import { readSearchParams } from "utils/customHooks"
import { queryClient } from "utils/infrastructure/query-client"

const TIME_REQUEST_QUERY_KEY = "TIME_REQUEST_QUERY_KEY"

function createKey(timeRequestHash?: string) {
    return [TIME_REQUEST_QUERY_KEY, timeRequestHash]
}

function createStatusKey(hash?: string, currentStatus?: string | null | undefined) {
    if (!currentStatus) {
        return createKey(hash)
    }
    return [TIME_REQUEST_QUERY_KEY, hash, currentStatus]
}

function useGetTimeRequest(hash?: string) {
    return useQuery(
        createKey(hash),
        () => {
            if (!hash) return
            return getTimeRequest(hash).then((r) => {
                timeRequestCreatedCallbacks.call(r)
                return r
            })
        },
        {
            enabled: !!hash,
            refetchOnWindowFocus: false,
            staleTime: Infinity,
            cacheTime: 0,
        }
    )
}

function getTimeRequestHashFromSearchParams() {
    return readSearchParams().get("time_request_hash") ?? undefined
}

function useIsTimeRequestLoading() {
    return useIsFetching({ queryKey: [TIME_REQUEST_QUERY_KEY] })
}

const timeRequestCreatedCallbacks: Callbacks<GetTimeRequestResponse> = {
    callbacks: [],
    call: (data) => {
        while (timeRequestCreatedCallbacks.callbacks.length) {
            timeRequestCreatedCallbacks.callbacks.pop()?.(data)
        }
    },
    add: (fn) => {
        timeRequestCreatedCallbacks.callbacks.push(fn)
    },
}

function useGetUpdatedTimeRequest(hash?: string, currentStatus?: string) {
    return useQuery(
        createStatusKey(hash, currentStatus),
        () => {
            if (!hash || !currentStatus) return
            if (currentStatus === "created") {
                return waitTimeRequestStatusUpdate(hash, currentStatus).then((result) => {
                    createdTimeRequestLoader.setQueryData(result)
                    return result
                })
            }
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!hash && !!currentStatus,
        }
    )
}

function setTimeRequestQueryData(timeRequestDetails: GetTimeRequestResponse) {
    queryClient.setQueryData(createKey(timeRequestDetails.hash), timeRequestDetails)
}

function getTimeRequestQueryData(timeRequestHash: string) {
    return queryClient.getQueryData<GetTimeRequestResponse>(createKey(timeRequestHash))
}

function updateTimeRequestDetailsQueryData(
    timeRequestHash: string,
    timeRequestDetails: Partial<GetTimeRequestResponse>
) {
    const data = getTimeRequestQueryData(timeRequestHash)
    if (!data) return
    setTimeRequestQueryData({ ...data, ...timeRequestDetails })
}

export const createdTimeRequestLoader = {
    useGet: useGetTimeRequest,
    getHash: getTimeRequestHashFromSearchParams,
    useIsLoading: useIsTimeRequestLoading,
    onSuccess: timeRequestCreatedCallbacks.add,
    setQueryData: setTimeRequestQueryData,
    getQueryData: getTimeRequestQueryData,
    useGetUpdated: useGetUpdatedTimeRequest,
    updateData: updateTimeRequestDetailsQueryData,
}
